import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';

const GUIDE_GAP = 20;
const GUIDE_PADDING = 8;
const GUIDE_EDGE_MARGIN = 16;
const GUIDE_CARD_ESTIMATED_HEIGHT = 190; // used only to decide/anchor placement; actual height is CSS auto
const GUIDE_ACCENT = '#64b5f6';

/**
 * Target refs for steps whose elements are marked data-guide="<name>" in the page (often inside child
 * components, where a React ref is awkward). A step's `target` may also be a list of names: the first one
 * on the page is used, e.g. ['skills', 'profile-tabs'] when the skills section is not shown yet.
 */
export function guideTargets(steps) {
  const find = (names) => names.map((n) => document.querySelector(`[data-guide="${n}"]`)).find(Boolean) || null;
  return Object.fromEntries(steps.map((st) => {
    const names = [].concat(st.target || []);
    return [names.join('|'), { get current() { return find(names); } }];
  }));
}

/**
 * Persisted open/step state plus target-rect measurement for a page tour.
 * `targetRefs` maps each step's `target` name to a ref on the DOM element to
 * spotlight. Pass `recalcDeps` for any layout-affecting state (sidebar open,
 * active tab, etc.) that should trigger a re-measure.
 */
export function useGuideTour({ storageKey, steps, targetRefs, recalcDeps = [], autoOpen = true }) {
  // autoOpen=false: the tour opens only from the page's "?" button, never by itself
  const [open, setOpen] = useState(() => {
    if (!autoOpen) return false;
    try {
      return !localStorage.getItem(storageKey);
    } catch (e) {
      return true;
    }
  });
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const show = () => {
    setStep(0);
    setOpen(true);
  };

  // persist=true means the tour should never auto-open again (finished, or "Don't show this again")
  const close = (persist) => {
    if (persist) {
      try {
        localStorage.setItem(storageKey, '1');
      } catch (e) {
        // ignore — worst case the tour auto-opens again next visit
      }
    }
    setOpen(false);
  };

  const next = () => setStep((s) => s + 1);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const targetName = [].concat(steps[step]?.target || []).join('|');
    const el = targetRefs[targetName]?.current;

    const measure = () => {
      if (!el) {
        setTargetRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, ...recalcDeps]);

  return { open, step, targetRect, show, next, close };
}

/**
 * Spotlight tour overlay: dims the page, cuts a static outlined "hole" around
 * the current step's target so it keeps its normal (white) background, and
 * draws an animated arrow from a dark guide card to it. The hole moves
 * smoothly between steps but never pulses/expands. Never blocks interaction
 * with the underlying page — pointer events stay off everywhere except the
 * card's own controls. `onClose(persist)` is called with persist=true when
 * the user should never see the tour auto-open again (finishing it, or
 * "Don't show this again").
 */
export default function GuideTour({ steps, step, onNext, onClose, targetRect, ariaLabel = 'Page guide' }) {
  const guideStep = steps[step];
  const isLastStep = step === steps.length - 1;
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const hasTarget = !!targetRect;
  const spot = hasTarget
    ? {
        top: targetRect.top - GUIDE_PADDING,
        left: targetRect.left - GUIDE_PADDING,
        width: targetRect.width + GUIDE_PADDING * 2,
        height: targetRect.height + GUIDE_PADDING * 2,
      }
    : null;

  const cardWidth = Math.min(420, viewport.w - 32);
  const neededSpace = GUIDE_CARD_ESTIMATED_HEIGHT + GUIDE_GAP + GUIDE_EDGE_MARGIN;
  const spaceBelow = hasTarget ? viewport.h - (spot.top + spot.height) : 0;
  const spaceAbove = hasTarget ? spot.top : 0;
  // Prefer hugging the target above/below it; if the target leaves no room on
  // either side (e.g. it spans nearly the full viewport), dock the card to the
  // bottom of the screen instead so it's always fully visible.
  const placement = !hasTarget
    ? 'below'
    : spaceBelow >= neededSpace
    ? 'below'
    : spaceAbove >= neededSpace
    ? 'above'
    : 'docked';
  const placeBelow = placement !== 'above';

  let cardLeft = (viewport.w - cardWidth) / 2;
  let cardTop = null;
  let cardBottom = null;
  let cardEdge = null;
  let targetAnchor = null;

  if (hasTarget) {
    const rawLeft = spot.left + spot.width / 2 - cardWidth / 2;
    cardLeft = Math.min(Math.max(rawLeft, GUIDE_EDGE_MARGIN), viewport.w - cardWidth - GUIDE_EDGE_MARGIN);

    if (placement === 'below') {
      cardTop = spot.top + spot.height + GUIDE_GAP;
      cardEdge = { y: cardTop };
    } else if (placement === 'above') {
      cardBottom = viewport.h - spot.top + GUIDE_GAP;
      cardEdge = { y: spot.top - GUIDE_GAP };
    } else {
      cardBottom = GUIDE_EDGE_MARGIN;
      cardEdge = { y: viewport.h - GUIDE_EDGE_MARGIN - GUIDE_CARD_ESTIMATED_HEIGHT };
    }
    cardEdge.x = Math.min(Math.max(spot.left + spot.width / 2, cardLeft + 24), cardLeft + cardWidth - 24);

    targetAnchor = {
      x: Math.min(Math.max(cardEdge.x, spot.left), spot.left + spot.width),
      y: placeBelow ? spot.top + spot.height : spot.top,
    };
  }

  return (
    // 1800 is intentionally above the app's global top bar (zIndex 1500) and
    // its in-app dialogs (1600/1700) — otherwise the bar/dialogs paint over
    // the guide whenever a spotlighted target sits near the top of the page.
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1800, pointerEvents: 'none' }}>
      {/* Dimmed backdrop with a cut-out around the target, so it keeps its normal
          (white) background and reads as "in focus" — static outline, no pulsing */}
      <motion.div
        initial={false}
        animate={spot || { top: viewport.h / 2, left: viewport.w / 2, width: 0, height: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          borderRadius: 14,
          boxShadow: '0 0 0 9999px rgba(8, 11, 20, 0.72)',
          border: spot ? `2px solid ${GUIDE_ACCENT}` : 'none',
        }}
      />

      {/* Arrow connecting the guide card to the target */}
      {spot && targetAnchor && cardEdge && (
        <svg width={viewport.w} height={viewport.h} style={{ position: 'fixed', top: 0, left: 0, overflow: 'visible' }}>
          <defs>
            <marker id="guideArrowHead" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill={GUIDE_ACCENT} />
            </marker>
          </defs>
          <motion.line
            key={`line-${step}`}
            x1={cardEdge.x}
            y1={cardEdge.y}
            x2={targetAnchor.x}
            y2={targetAnchor.y}
            stroke={GUIDE_ACCENT}
            strokeWidth={2.5}
            strokeDasharray="6 6"
            markerEnd="url(#guideArrowHead)"
            initial={{ strokeDashoffset: 24 }}
            animate={{ strokeDashoffset: 0 }}
            transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      )}

      {/* Guide content card — dark background so it reads clearly over the dimmed page */}
      <Box
        component={motion.div}
        key={step}
        initial={{ opacity: 0, y: placeBelow ? -8 : 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        aria-label={ariaLabel}
        sx={{
          position: 'fixed',
          left: cardLeft,
          top: cardTop ?? undefined,
          bottom: cardBottom ?? undefined,
          width: cardWidth,
          maxWidth: 'calc(100vw - 32px)',
          pointerEvents: 'auto',
          bgcolor: '#0f172a',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          px: 3,
          py: 2.5,
        }}
      >
        <Typography variant="overline" sx={{ color: GUIDE_ACCENT, fontWeight: 700 }}>
          {guideStep.label} {step + 1} of {steps.length}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
          {guideStep.title}
        </Typography>
        {guideStep.description && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
            {guideStep.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2 }}>
          <Button
            size="small"
            onClick={() => onClose(true)}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '0.75rem', px: 0.5 }}
          >
            Don't show this again
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button size="small" onClick={() => onClose(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Skip</Button>
            <Button size="small" variant="contained" onClick={isLastStep ? () => onClose(true) : onNext}>
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
